import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  Shield, AlertTriangle, Clock, CheckCircle2, Loader2,
  Building2, Calendar, ChevronDown, ChevronUp, Timer
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (a) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(a || 0);

function StatusCard({ count, amount, label, color, icon: Icon }) {
  const colors = {
    red: { bg: "bg-[#FEF2F2]", text: "text-[#DC2626]", border: "border-[#FECACA]" },
    amber: { bg: "bg-[#FFF7ED]", text: "text-[#D97706]", border: "border-[#FED7AA]" },
    green: { bg: "bg-[#F0FDF4]", text: "text-[#059669]", border: "border-[#BBF7D0]" },
  };
  const c = colors[color];
  return (
    <div className={`rounded-xl border ${c.border} ${c.bg} p-4`}>
      <div className={`flex items-center gap-2 ${c.text} mb-2`}>
        <Icon className="w-5 h-5" />
        <span className="text-xs font-semibold uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-bold text-[#111827]">{count}</p>
      <p className="text-sm text-[#6B7280] mt-0.5">{fmt(amount)}</p>
    </div>
  );
}

function InvoiceRow({ inv }) {
  const [expanded, setExpanded] = useState(false);
  const statusColors = {
    overdue: "bg-[#FEE2E2] text-[#DC2626]",
    at_risk: "bg-[#FEF3C7] text-[#D97706]",
    safe: "bg-[#D1FAE5] text-[#059669]",
  };

  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden" data-testid={`msme-invoice-${inv.invoice_id}`}>
      <div
        className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-[#FAFBFC] transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="w-9 h-9 rounded-lg bg-[#EFF6FF] flex items-center justify-center flex-shrink-0">
          <Building2 className="w-4 h-4 text-[#2563EB]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[#111827] truncate">{inv.vendor_name}</p>
          <p className="text-xs text-[#6B7280]">{inv.invoice_number} &middot; {inv.invoice_date}</p>
        </div>
        <div className="text-right flex-shrink-0 mr-2">
          <p className="text-sm font-bold text-[#111827]">{fmt(inv.amount)}</p>
          <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[inv.status]}`}>
            {inv.days_elapsed} days
          </span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-[#9CA3AF]" /> : <ChevronDown className="w-4 h-4 text-[#9CA3AF]" />}
      </div>
      {expanded && (
        <div className="px-4 py-3 border-t border-[#F3F4F6] bg-[#FAFBFC] text-sm space-y-1">
          <div className="flex justify-between"><span className="text-[#6B7280]">Invoice Date</span><span>{inv.invoice_date}</span></div>
          <div className="flex justify-between"><span className="text-[#6B7280]">Due Date</span><span>{inv.due_date || "—"}</span></div>
          <div className="flex justify-between"><span className="text-[#6B7280]">Days Elapsed</span><span className="font-medium">{inv.days_elapsed}</span></div>
          <div className="flex justify-between"><span className="text-[#6B7280]">Days Remaining (45-day rule)</span><span className={`font-medium ${inv.days_remaining === 0 ? "text-[#DC2626]" : "text-[#D97706]"}`}>{inv.days_remaining}</span></div>
          <div className="flex justify-between"><span className="text-[#6B7280]">Payment Status</span><span>{inv.payment_status}</span></div>
        </div>
      )}
    </div>
  );
}

export default function MSMECompliancePage() {
  const { defaultCompanyId } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (defaultCompanyId) params.company_id = defaultCompanyId;
      const res = await axios.get(`${API}/msme/dashboard`, { params, withCredentials: true });
      setData(res.data);
    } catch {
      toast.error("Failed to load MSME data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#2563EB]" /></div>;
  }

  const summary = data?.summary || {};

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8" data-testid="msme-compliance-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#111827] flex items-center gap-2">
            <Shield className="w-6 h-6 text-[#2563EB]" /> MSME Compliance
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">
            Track payments to MSME vendors under the 45-day payment rule (Section 43B(h))
          </p>
        </div>

        {/* Info Banner */}
        <div className="p-4 bg-[#EFF6FF] rounded-xl border border-[#BFDBFE] mb-6">
          <p className="text-sm text-[#1E40AF]">
            <strong>45-Day Rule:</strong> Under Section 43B(h) of the Income Tax Act, payments to MSME-registered vendors must be made within 45 days. Non-compliance results in disallowance of the expense for tax purposes.
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="msme-summary">
          <StatusCard count={summary.overdue_count || 0} amount={summary.overdue_amount || 0} label="Overdue (>45 days)" color="red" icon={AlertTriangle} />
          <StatusCard count={summary.at_risk_count || 0} amount={summary.at_risk_amount || 0} label="At Risk (30-45 days)" color="amber" icon={Timer} />
          <StatusCard count={summary.safe_count || 0} amount={0} label="Within Limit" color="green" icon={CheckCircle2} />
        </div>

        {data?.msme_vendor_count === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-[#E5E7EB]" data-testid="no-msme-vendors">
            <Shield className="w-12 h-12 text-[#D1D5DB] mx-auto mb-3" />
            <p className="text-[#374151] font-medium">No MSME vendors tagged</p>
            <p className="text-sm text-[#6B7280] mt-1">
              Go to Masters &rarr; Customers and mark vendors as MSME-registered to enable compliance tracking.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Overdue Section */}
            {data?.overdue_invoices?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-[#DC2626] mb-3 flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4" /> Overdue ({data.overdue_invoices.length})
                </h3>
                <div className="space-y-2">
                  {data.overdue_invoices.map(inv => <InvoiceRow key={inv.invoice_id} inv={inv} />)}
                </div>
              </div>
            )}

            {/* At Risk */}
            {data?.at_risk_invoices?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-[#D97706] mb-3 flex items-center gap-1.5">
                  <Timer className="w-4 h-4" /> At Risk ({data.at_risk_invoices.length})
                </h3>
                <div className="space-y-2">
                  {data.at_risk_invoices.map(inv => <InvoiceRow key={inv.invoice_id} inv={inv} />)}
                </div>
              </div>
            )}

            {/* Safe */}
            {data?.within_limit_invoices?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-[#059669] mb-3 flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4" /> Within Limit ({data.within_limit_invoices.length})
                </h3>
                <div className="space-y-2">
                  {data.within_limit_invoices.map(inv => <InvoiceRow key={inv.invoice_id} inv={inv} />)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
